import { useDraggable } from '@dnd-kit/core';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { AnalyticsContextSpaceContent } from '@gitbook/app-analytics';
import type { RevisionPageComputed } from '@gitbook/hive-core';
import { useHiveRevision } from '@gitbook/hive-react';
import { type UsePaletteTrigger, usePaletteTrigger } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Badge, Button, TextSkeleton } from '@gitbook/spine-react';
import { sanitizeError } from '@gitbook/util-debug';
import { assertIsDefined } from '@gitbook/util-ts';

import {
    ComputedSourceIcon,
    ComputedSourceTitle,
    ContentSourcePagesBlankslate,
    getDragItem,
} from '../../../Components';
import { withPageCommands } from '../Page/withPageCommands';
import {
    PageGroupHighlight,
    PageGroupHighlightHeader,
    PageGroupHighlightLoadingState,
} from './PageGroupHighlight';

export const ComputedPagesContext = React.createContext<RevisionPageComputed | null>(null);

interface ComputedPageGroupProps {
    space: api.Space;
    page: RevisionPageComputed;
    children: React.ReactNode;
    readOnly: boolean;
    adaptiveContentContext: null;
    analyticsContext: AnalyticsContextSpaceContent;
}

/**
 * Visual indicator for a group of pages generated from a computed page.
 */
export function ComputedPagesGroup(props: ComputedPageGroupProps) {
    const { children } = props;
    const parentPage = React.useContext(ComputedPagesContext);

    if (parentPage) {
        // Don't render anything for nested computed pages
        // as they are being generated by a parent one, and only the top one should be editable.
        return children;
    }

    return <RenderedComputedPageGroup {...props} />;
}

const RenderedComputedPageGroup = withPageCommands<ComputedPageGroupProps>(
    function RenderedComputedPageGroupImpl(props) {
        const { space, page, children, readOnly, commandsPalette, commandsElement } = props;

        const { computations } = useHiveRevision();

        const computedState = computations.pages[page.id];
        assertIsDefined(computedState);

        const isEmpty = computedState.result?.rootPages.length === 0;
        const { error, complete } = computedState;

        const { opened, triggerProps } = usePaletteTrigger({ palette: commandsPalette });

        const drag = useDraggable({
            disabled: readOnly,
            ...getDragItem({
                type: 'page',
                id: page.id,
                pageId: page.id,
                space: space.id,
                previewLabel: page.title,
                previewIcon: 'Zap',
            }),
        });

        return (
            <ComputedPagesContext.Provider value={page}>
                {commandsElement}
                <PageGroupHighlight ref={drag.setNodeRef} {...drag.listeners} isActive={opened}>
                    {children}
                    {error ? (
                        <ComputedPageErrorState error={error} />
                    ) : !complete ? (
                        <PageGroupHighlightLoadingState />
                    ) : isEmpty ? (
                        <ComputedPageEmptyBlankslate space={space} source={page.computed} />
                    ) : null}

                    <React.Suspense fallback={null}>
                        <PageGroupHighlightHeader>
                            {readOnly ? (
                                <ComputedPageBadge page={page} />
                            ) : (
                                <ComputedPageButton page={page} triggerProps={triggerProps} />
                            )}
                        </PageGroupHighlightHeader>
                    </React.Suspense>
                </PageGroupHighlight>
            </ComputedPagesContext.Provider>
        );
    }
);

function ComputedPageBadge(props: { page: RevisionPageComputed }) {
    const { page } = props;
    const source = page.computed;
    return (
        <ComputedSourceTitle source={source}>
            {({ title }) => (
                <Badge
                    size="small"
                    label={title}
                    icon={<ComputedSourceIcon source={page.computed} />}
                />
            )}
        </ComputedSourceTitle>
    );
}

function ComputedPageButton(props: {
    page: RevisionPageComputed;
    triggerProps: UsePaletteTrigger['triggerProps'];
}) {
    const { page, triggerProps } = props;
    return (
        <ComputedSourceTitle source={page.computed}>
            {({ title }) => (
                <Button
                    ref={triggerProps.ref}
                    onPress={triggerProps.onPress}
                    disabled={triggerProps.disabled}
                    onClick={(e) => e.preventDefault()}
                    size="small"
                    label={title}
                    tooltip={{ label: 'Actions' }}
                    leadingIcon={<ComputedSourceIcon source={page.computed} />}
                />
            )}
        </ComputedSourceTitle>
    );
}

function ComputedPageErrorState(props: { error: Error }) {
    const { error } = props;

    return (
        <p className="px-4 py-2 text-ui-small">
            <Icon.Warning className="-mt-0.5 mr-1.5" />
            {sanitizeError(error)}
        </p>
    );
}

function ComputedPageEmptyBlankslate(props: {
    space: api.Space;
    source: api.ComputedContentSourceRevision;
}) {
    const { space, source } = props;

    return (
        <div className="px-4 py-2 text-muted text-ui-small">
            <React.Suspense
                fallback={<TextSkeleton className="text-ui-small">Loading Page</TextSkeleton>}
            >
                <ContentSourcePagesBlankslate space={space} source={source} />
            </React.Suspense>
        </div>
    );
}
